
/**
 * Trustpilot Review Widget Modal - Modal Component
 * Handles modal functionality for displaying review media (images/videos)
 * Maintains singleton pattern for global access by other components
 */

class TrustpilotReviewWidgetModal extends HTMLElement {
  constructor() {
    super();
    
    this.modal = null;
    this.activeComponent = null;
    this.lastFocusedElement = null;
    this.isInitialized = false;
    this.focusTrapHandlers = {};
    
    /** ADA Compliance: Configurable announcement messages */
    this.announcements = {
      opened: (mediaType, current, total) => {
        const type = mediaType === 'video' ? 'video' : 'image';
        return `Review ${type} modal opened. ${current} of ${total} ${type}${total === 1 ? '' : 's'}.`;
      },
      closed: 'Review modal closed.',
      nextMedia: (current, total, mediaType) => {
        const type = mediaType === 'video' ? 'video' : 'image';
        return `Showing ${type} ${current} of ${total}.`;
      },
      prevMedia: (current, total, mediaType) => {
        const type = mediaType === 'video' ? 'video' : 'image';
        return `Showing ${type} ${current} of ${total}.`;
      }
    };
  }

  connectedCallback() {
    this.modal = this.querySelector('[data-tp-modal]');
    if (!this.modal) return;

    this.init();
    this.exposeGlobalInstance();
  }

  disconnectedCallback() {
    if (this.handleOutsideClick) {
      window.removeEventListener('click', this.handleOutsideClick);
    }
    this.removeFocusTrap();
  }

  init() {
    if (this.isInitialized) return;

    this.modal.dataset.globalInit = "true";
    
    const closeBtn = this.modal.querySelector('[data-tp-close]');
    const prevBtn = this.modal.querySelector('[data-tp-modal-prev-btn]');
    const nextBtn = this.modal.querySelector('[data-tp-modal-next-btn]');

    if (closeBtn) {
      const handleClose = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
        if (this.activeComponent) {
          this.activeComponent.closeModal();
        }
      };
      
      closeBtn.addEventListener('mousedown', handleClose, true);
      closeBtn.addEventListener('click', handleClose, true);
      
      closeBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClose(e);
        }
      }, true);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.activeComponent?.prevMedia();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.activeComponent?.nextMedia();
      });
    }

    this.modal.addEventListener('keydown', (e) => {
      if (!this.modal.classList.contains('active')) return;
      
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.activeComponent?.closeModal();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.activeComponent?.nextMedia();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.activeComponent?.prevMedia();
      }
    });

    this.handleOutsideClick = (e) => {
      if (this.modal.classList.contains('active') && 
          e.target === this.modal && 
          !this.modal.querySelector('.tp-modal-content')?.contains(e.target)) {
        this.activeComponent?.closeModal();
      }
    };
    window.addEventListener('click', this.handleOutsideClick);

    this.isInitialized = true;
  }

  /** Set window.trustpilotReviewModal so tab component can open/close modal. */
  exposeGlobalInstance() {
    if (typeof window !== 'undefined') {
      window.trustpilotReviewModal = this;
    }
  }

  /** Show modal with current image/video; trap focus and set aria-hidden on main/header/footer. */
  open(component, currentIndex, mediaItems) {
    if (!this.modal) return;

    this.lastFocusedElement = document.activeElement;
    this.activeComponent = component;
    
    if (component) {
      component.currentIndex = currentIndex;
      component.currentItems = mediaItems;
      component.updateModalContent();
    }

    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    document.body.style.overflowY = 'hidden';
    document.documentElement.style.overflowY = 'hidden';
    document.documentElement.classList.add('tp-modal-open');
    document.body.classList.add('tp-modal-open');
    
    /** Set aria-hidden on all other elements for screen readers */
    this.setAriaHidden(true);
    
    setTimeout(() => {
      const closeBtn = this.modal.querySelector('[data-tp-close]');
      if (closeBtn) {
        this.trapFocus(this.modal, closeBtn);
      } else {
        this.trapFocus(this.modal);
      }
      
      if (component) {
        const currentItem = component.currentItems[currentIndex];
        const mediaType = currentItem ? (currentItem.getAttribute('data-media-type') || 'image') : 'image';
        const total = component.currentItems.length;
        this.announce(this.announcements.opened(mediaType, currentIndex + 1, total), 'assertive');
      }
    }, 100);
  }

  /** Hide modal, clear focus trap, restore focus to last focused element. */
  close() {
    if (!this.modal) return;

    this.removeFocusTrap();
    this.announce(this.announcements.closed);
    this.setAriaHidden(false);

    this.modal.classList.remove('active');
    
    const mediaContainer = this.modal.querySelector('[data-tp-modal-media-container]');
    if (mediaContainer) {
      const mediaInner = mediaContainer.querySelector('img, video');
      if (mediaInner) mediaInner.remove();
    }
    
    document.body.style.overflow = '';
    document.body.style.overflowY = '';
    document.documentElement.style.overflowY = '';
    document.documentElement.classList.remove('tp-modal-open');
    document.body.classList.remove('tp-modal-open');
    
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
  }

  /** Set aria-hidden and pointer-events on main, header, footer when modal is open (screen readers). */
  setAriaHidden(hide) {
    const mainContent = document.querySelector('main');
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    
    [mainContent, header, footer].forEach((el) => {
      if (el) {
        if (hide) {
          el.setAttribute('aria-hidden', 'true');
          el.style.pointerEvents = 'none';
        } else {
          el.removeAttribute('aria-hidden');
          el.style.pointerEvents = '';
        }
      }
    });
  }

  /** Helpers */
  isMobile() {
    return window.innerWidth <= 1024;
  }

  getFocusableElements(container) {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex^="-"])',
      '[role="button"]',
      'video[controls]'
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors)).filter(
      (el) => {
        if (el.getAttribute('tabindex') === '-1') return false;
        return !el.hasAttribute('aria-hidden') && el.offsetParent !== null;
      }
    );
  }

  /** Keep focus inside modal; Tab/Shift+Tab cycle, first focus on elementToFocus or first focusable. */
  trapFocus(container, elementToFocus) {
    const elements = this.getFocusableElements(container);
    if (elements.length === 0) {
      container.setAttribute('tabindex', '-1');
      container.focus();
      return;
    }

    const first = elements[0];
    const last = elements[elements.length - 1];
    const focusTarget = elementToFocus && elements.includes(elementToFocus) ? elementToFocus : first;

    this.removeFocusTrap();

    this.focusTrapHandlers.focusin = (event) => {
      if (!container.contains(event.target) && container.classList.contains('active')) {
        event.preventDefault();
        event.stopPropagation();
        focusTarget.focus();
      }
    };

    this.focusTrapHandlers.keydown = (event) => {
      if (event.key !== 'Tab' || !container.classList.contains('active')) return;

      const activeElement = document.activeElement;

      if (!container.contains(activeElement)) {
        event.preventDefault();
        focusTarget.focus();
        return;
      }

      const currentElements = this.getFocusableElements(container);
      if (currentElements.length === 0) return;

      const currentFirst = currentElements[0];
      const currentLast = currentElements[currentElements.length - 1];
      const currentIndex = currentElements.indexOf(activeElement);

      if (event.shiftKey) {
        if (currentIndex <= 0 || activeElement === currentFirst) {
          event.preventDefault();
          currentLast.focus();
        }
      } else {
        if (currentIndex >= currentElements.length - 1 || activeElement === currentLast) {
          event.preventDefault();
          currentFirst.focus();
        }
      }
    };

    document.addEventListener('focusin', this.focusTrapHandlers.focusin, true);
    document.addEventListener('keydown', this.focusTrapHandlers.keydown, true);

    focusTarget.focus();
  }

  /** Remove focus trap listeners. */
  removeFocusTrap() {
    if (this.focusTrapHandlers.focusin) {
      document.removeEventListener('focusin', this.focusTrapHandlers.focusin, true);
    }

    if (this.focusTrapHandlers.keydown) {
      document.removeEventListener('keydown', this.focusTrapHandlers.keydown, true);
    }
    
    this.focusTrapHandlers = {};
  }

  announce(message, priority = 'polite', clearAfter = true) {
    const liveRegion = document.getElementById('trustpilot-aria-live');
    if (!liveRegion) return;
    
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.textContent = '';
    
    setTimeout(() => {
      liveRegion.textContent = message;
      
      /** Clear the message after it's been announced (for screen readers) */
      /** Wait longer for assertive messages as they need more time to be read */
      if (clearAfter) {
        const clearDelay = priority === 'assertive' ? 2000 : 1500;
        setTimeout(() => {
          liveRegion.textContent = '';
        }, clearDelay);
      }
    }, 100);
  }
}

if (!customElements.get('trustpilot-review-widget-modal')) {
  customElements.define('trustpilot-review-widget-modal', TrustpilotReviewWidgetModal);
}
